/**
 * PII scrubber — mascara campos sensíveis em payloads Sentry.
 *
 * Cobre:
 * - `extra.*`, `tags.*`, `contexts.*`, `request.data`, `breadcrumbs[].data`
 * - Detecção por NOME de chave (case-insensitive).
 * - Não inspeciona conteúdo livre de strings (regex de email/CPF) por custo+falso-positivo;
 *   se algo PII cair em mensagem livre, escalar caso a caso via `beforeSend` custom.
 *
 * Alinhado com IR-SEC-* (sem PII em telemetria) e GAP-5 do doc Entercast Sales Leads.
 */

const DEFAULT_SENSITIVE_FIELDS = [
  "email",
  "e-mail",
  "phone",
  "telefone",
  "celular",
  "cpf",
  "cnpj",
  "rg",
  "password",
  "senha",
  "token",
  "access_token",
  "refresh_token",
  "secret",
  "api_key",
  "apikey",
  "authorization",
];

const MASK = "[Filtered]";

export interface ScrubOptions {
  additionalFields?: string[];
}

export function buildScrubber(options: ScrubOptions = {}) {
  const sensitive = new Set(
    [...DEFAULT_SENSITIVE_FIELDS, ...(options.additionalFields ?? [])].map((f) =>
      f.toLowerCase(),
    ),
  );

  function isSensitiveKey(key: string): boolean {
    const lower = key.toLowerCase();
    if (sensitive.has(lower)) return true;
    // Match parcial — campos como `userEmail`, `user_phone`, etc.
    for (const field of sensitive) {
      if (lower.includes(field)) return true;
    }
    return false;
  }

  function scrubValue(value: unknown, depth = 0): unknown {
    if (depth > 6) return value; // safety: evita stack overflow em ciclos
    if (value === null || value === undefined) return value;
    if (Array.isArray(value)) return value.map((v) => scrubValue(v, depth + 1));
    if (typeof value === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = isSensitiveKey(k) ? MASK : scrubValue(v, depth + 1);
      }
      return out;
    }
    return value;
  }

  return function scrub<T>(event: T): T {
    if (!event || typeof event !== "object") return event;
    return scrubValue(event) as T;
  };
}

export const defaultScrubber = buildScrubber();
